import { useCallback } from 'react';
import { useCookieConsent } from './useCookieConsent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type GAEventParams = {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
};

/**
 * Hook for Google Analytics 4 event tracking with consent awareness.
 * Events are only tracked if the user has given analytics consent.
 */
export const useGoogleAnalytics = () => {
  const { isAllowed } = useCookieConsent();

  const trackEvent = useCallback(
    ({ action, category, label, value, ...rest }: GAEventParams) => {
      const analyticsAllowed = isAllowed('analytics');
      const gtagExists = typeof window.gtag === 'function';
      
      console.log('[GA4] trackEvent called:', {
        action,
        category,
        label,
        analyticsAllowed,
        gtagExists,
      });
      
      if (!analyticsAllowed || !gtagExists) {
        console.log('[GA4] Event not sent - consent:', analyticsAllowed, 'gtag:', gtagExists);
        return;
      }

      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value,
        ...rest,
      });
      
      console.log('[GA4] Event sent successfully:', action);
    },
    [isAllowed]
  );

  const trackPageView = useCallback(
    (pagePath: string, pageTitle?: string) => {
      if (!isAllowed('analytics') || typeof window.gtag !== 'function') {
        return;
      }

      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle || document.title,
      });
    },
    [isAllowed]
  );

  // Pre-defined event helpers
  const trackWhatsAppClick = useCallback(
    (source: string, language: string) => {
      trackEvent({
        action: 'whatsapp_click',
        category: 'contact',
        label: source,
        language,
      });
    },
    [trackEvent]
  );

  const trackFormStart = useCallback(
    (formType: string, language: string) => {
      trackEvent({
        action: 'form_start',
        category: 'engagement',
        label: formType,
        language,
      });
    },
    [trackEvent]
  );

  const trackFormSubmit = useCallback(
    (formType: string, language: string, treatment?: string) => {
      trackEvent({
        action: 'generate_lead',
        category: 'conversion',
        label: formType,
        language,
        treatment,
      });
    },
    [trackEvent]
  );

  const trackPhoneClick = useCallback(
    (language: string) => {
      trackEvent({
        action: 'phone_click',
        category: 'contact',
        language,
      });
    },
    [trackEvent]
  );

  return {
    trackEvent,
    trackPageView,
    trackWhatsAppClick,
    trackFormStart,
    trackFormSubmit,
    trackPhoneClick,
    isEnabled: isAllowed('analytics'),
  };
};
